import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';

const ETA_OPTIONS = [
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
];

function formatCountdown(seconds) {
  if (seconds <= 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function OnMyWay() {
  const { t } = useTranslation();
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  const [selectedETA, setSelectedETA] = useState(30);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState(null);

  const fetchBooking = useCallback(async () => {
    try {
      const res = await api.getPremiumBooking(bookingId);
      setBooking(res.data || res);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  // Initial fetch + polling every 10 seconds
  useEffect(() => {
    fetchBooking();
    const interval = setInterval(fetchBooking, 10000);
    return () => clearInterval(interval);
  }, [fetchBooking]);

  // Countdown timer for en_route state
  useEffect(() => {
    if (!booking || booking.status !== 'en_route' || !booking.estimated_arrival) return;

    const calcRemaining = () => {
      const arrival = new Date(booking.estimated_arrival).getTime();
      const now = Date.now();
      return Math.max(0, Math.floor((arrival - now) / 1000));
    };

    setCountdown(calcRemaining());
    const interval = setInterval(() => {
      setCountdown(calcRemaining());
    }, 1000);

    return () => clearInterval(interval);
  }, [booking]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await api.triggerOnMyWay(bookingId, selectedETA);
      await fetchBooking();
    } catch (err) {
      setError(err.message || 'Failed to confirm');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateETA = async () => {
    const newEta = prompt('Enter new ETA in minutes:', '15');
    if (!newEta || isNaN(Number(newEta))) return;
    try {
      await api.updateETA(bookingId, Number(newEta));
      await fetchBooking();
    } catch (err) {
      setError(err.message || 'Failed to update ETA');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(t('premium.confirmCancelRoute', 'Are you sure you want to cancel?'))) return;
    try {
      await api.cancelOnMyWay(bookingId);
      navigate(-1);
    } catch (err) {
      setError(err.message || 'Failed to cancel');
    }
  };

  // Progress calculation: uses eta_triggered_at (NOT on_my_way_at)
  const getProgress = () => {
    if (!booking || !booking.estimated_arrival || !booking.eta_triggered_at) return 0;
    const start = new Date(booking.eta_triggered_at).getTime();
    const end = new Date(booking.estimated_arrival).getTime();
    const now = Date.now();
    const total = end - start;
    if (total <= 0) return 100;
    const elapsed = now - start;
    return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  };

  // --- Loading ---
  if (loading) {
    return (
      <div style={{ padding: '7rem 1.5rem 5rem', textAlign: 'center' }}>
        <div
          style={{
            width: 36,
            height: 36,
            border: '3px solid var(--border)',
            borderTopColor: 'var(--orange)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>
          {t('common.loading', 'Loading...')}
        </span>
      </div>
    );
  }

  // --- Error with no booking ---
  if (error && !booking) {
    return (
      <div style={{ padding: '7rem 1.5rem 5rem', textAlign: 'center' }}>
        <div style={{ color: '#ef4444', fontSize: 16, marginBottom: 16 }}>{error}</div>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '12px 24px',
            borderRadius: 14,
            border: '1px solid var(--border)',
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('common.goBack', 'Go Back')}
        </button>
      </div>
    );
  }

  // --- STATE 2: En Route Tracker ---
  if (booking && booking.status === 'en_route') {
    const progress = getProgress();
    const timelineEvents = [
      { label: t('premium.onMyWayConfirmed', 'On my way confirmed'), active: true },
      { label: t('premium.artistNotified', 'Artist notified'), active: true },
      { label: t('premium.artistPreparing', 'Artist preparing'), active: progress > 30 },
      {
        label: t('premium.arrivalETA', 'Arrival ETA: {{time}}', {
          time: booking.estimated_arrival
            ? new Date(booking.estimated_arrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '--:--',
        }),
        active: progress > 80,
      },
    ];

    return (
      <div style={{ padding: '7rem 1.5rem 5rem', maxWidth: 480, margin: '0 auto' }}>
        <h1
          style={{
            fontSize: 28,
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: 8,
            color: 'var(--text-primary)',
            letterSpacing: -0.5,
          }}
        >
          {t('premium.enRoute', 'En Route')}
        </h1>
        <p
          style={{
            fontSize: 14,
            color: 'var(--text-muted)',
            textAlign: 'center',
            marginBottom: 32,
            lineHeight: 1.5,
          }}
        >
          {t('premium.enRouteDescription', 'Your artist has been notified and is preparing for your arrival.')}
        </p>

        {error && (
          <div style={{ color: '#ef4444', fontSize: 13, textAlign: 'center', marginBottom: 12 }}>{error}</div>
        )}

        {/* Status card */}
        <div
          style={{
            backgroundColor: 'var(--bg-card)',
            borderRadius: 16,
            padding: 24,
            border: '2px solid var(--orange)',
          }}
        >
          {/* Notified indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#22c55e' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#22c55e' }}>
              {t('premium.artistNotified', 'Artist Notified')}
            </span>
          </div>

          {/* Badge */}
          <div style={{ marginBottom: 8 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                borderRadius: 20,
                backgroundColor: 'color-mix(in srgb, var(--orange) 15%, transparent)',
                color: 'var(--orange)',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 1.5,
              }}
            >
              {t('premium.enRouteBadge', 'EN ROUTE')}
            </span>
          </div>

          {/* Countdown */}
          <div
            style={{
              fontSize: 56,
              fontWeight: 800,
              textAlign: 'center',
              color: 'var(--orange)',
              fontVariantNumeric: 'tabular-nums',
              margin: '16px 0',
            }}
          >
            {formatCountdown(countdown)}
          </div>

          {/* Progress bar */}
          <div
            style={{
              width: '100%',
              height: 6,
              backgroundColor: 'var(--border)',
              borderRadius: 3,
              overflow: 'hidden',
              margin: '16px 0',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: 'var(--orange)',
                borderRadius: 3,
                transition: 'width 1s linear',
              }}
            />
          </div>

          {/* Timeline */}
          <div style={{ marginTop: 20 }}>
            {timelineEvents.map((event, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0' }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: event.active ? 'var(--orange)' : 'var(--border)',
                    marginTop: 5,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 14,
                    color: event.active ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontWeight: event.active ? 600 : 400,
                  }}
                >
                  {event.label}
                </span>
              </div>
            ))}
          </div>

          {/* Artist message */}
          {booking.artist_message && (
            <div
              style={{
                backgroundColor: 'color-mix(in srgb, var(--orange) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--orange) 20%, transparent)',
                borderRadius: 12,
                padding: 14,
                marginTop: 16,
                fontSize: 14,
                color: 'var(--text-muted)',
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 600, marginBottom: 6 }}>
                {booking.artist_name || t('premium.yourArtist', 'Your Artist')}
              </div>
              {booking.artist_message}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={handleUpdateETA}
            style={{
              flex: 1,
              padding: 16,
              borderRadius: 14,
              border: '1px solid var(--border)',
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('premium.updateETA', 'Update ETA')}
          </button>
          <button
            onClick={handleCancel}
            style={{
              flex: 1,
              padding: 16,
              borderRadius: 14,
              border: '1px solid #331111',
              backgroundColor: 'rgba(220, 38, 38, 0.08)',
              color: '#ef4444',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('premium.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    );
  }

  // --- STATE 1: ETA Selection (window_reserved or default) ---
  return (
    <div style={{ padding: '7rem 1.5rem 5rem', maxWidth: 480, margin: '0 auto' }}>
      <h1
        style={{
          fontSize: 28,
          fontWeight: 700,
          textAlign: 'center',
          marginBottom: 8,
          color: 'var(--text-primary)',
          letterSpacing: -0.5,
        }}
      >
        {t('premium.imOnMyWay', "I'm On My Way")}
      </h1>
      <p
        style={{
          fontSize: 14,
          color: 'var(--text-muted)',
          textAlign: 'center',
          marginBottom: 32,
          lineHeight: 1.5,
        }}
      >
        {t(
          'premium.onMyWayDescription',
          'Let your artist know you are headed to the salon. Select your estimated arrival time so they can prepare for you.'
        )}
      </p>

      {error && (
        <div style={{ color: '#ef4444', fontSize: 13, textAlign: 'center', marginBottom: 12 }}>{error}</div>
      )}

      {/* ETA Cards */}
      <div style={{ display: 'flex', gap: 12 }}>
        {ETA_OPTIONS.map((opt) => {
          const isSelected = selectedETA === opt.value;
          return (
            <div
              key={opt.value}
              onClick={() => setSelectedETA(opt.value)}
              style={{
                flex: 1,
                padding: '24px 16px',
                borderRadius: 16,
                border: isSelected ? '2px solid var(--orange)' : '2px solid var(--border)',
                backgroundColor: isSelected
                  ? 'color-mix(in srgb, var(--orange) 10%, var(--bg-card))'
                  : 'var(--bg-card)',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 4 }}>
                {isSelected ? '\u2713' : '\u23F1'}
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: isSelected ? 'var(--orange)' : 'var(--text-primary)',
                }}
              >
                {opt.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Booking info card */}
      {booking && (
        <div
          style={{
            backgroundColor: 'var(--bg-card)',
            borderRadius: 16,
            padding: 20,
            marginTop: 24,
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            {booking.artist_avatar ? (
              <img
                src={booking.artist.avatar}
                alt={booking.artist.name}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid var(--orange)',
                }}
              />
            ) : (
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  border: '2px solid var(--orange)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'var(--border)',
                  fontSize: 18,
                  fontWeight: 700,
                  color: 'var(--orange)',
                }}
              >
                {(booking.artist_name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
                {booking.artist_name || t('premium.artistTBD', 'Artist TBD')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                {booking.service_names || ''}
              </div>
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {booking.booking_date && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: 'var(--text-muted)' }}>{t('premium.dateTime', 'Date & Time')}</span>
                <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                  {new Date(booking.booking_date).toLocaleDateString()} {booking.window_start} - {booking.window_end}
                </span>
              </div>
            )}
            {(booking.branch?.name || booking.branch_name) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span style={{ color: 'var(--text-muted)' }}>{t('premium.branch', 'Branch')}</span>
                <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                  {booking.branch_name}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm button */}
      <button
        onClick={handleConfirm}
        disabled={submitting}
        style={{
          width: '100%',
          padding: 18,
          borderRadius: 14,
          border: 'none',
          backgroundColor: 'var(--orange)',
          color: '#000',
          fontSize: 16,
          fontWeight: 800,
          letterSpacing: 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
          marginTop: 32,
          opacity: submitting ? 0.6 : 1,
        }}
      >
        {submitting
          ? t('premium.confirming', 'CONFIRMING...')
          : t('premium.confirmOnMyWay', "CONFIRM \u2014 I'M ON MY WAY")}
      </button>
    </div>
  );
}
